/*
 * Tree Selection State (Reducer)
 *
 * Purpose:
 * - Manage UI interaction state for the Vessel/Voyage/Route tree.
 * - Keep selection and expand/collapse behavior predictable and testable.
 *
 * Scope:
 * - Marked vessel: ui.markedVessel (single vessel, the app-wide "active" one)
 * - Selection state: selectedVesselIds, selectedVoyageIds, selectedRouteIds
 * - Expand state: expandedVesselIds, expandedVoyageIds
 *
 * Notes:
 * - UI-state ONLY. Entities/relations live in the RouteStore; the invariant
 *   "every selected non-draft route belongs to the single selected voyage"
 *   lets clearing work without relations.
 * - VESSEL_MARKED is an intent action: marking a different vessel atomically
 *   clears the previous vessel's selections in the same reducer step, so no
 *   effect coordination (skip flags, sync hooks) is needed around it.
 */

import {
  isDraftSelectionId,
  isParentlessSelectionId,
} from "@/features/route/state/RouteStore.jsx"

// id when present (panel/tree), else name (map-icon clicks carry no id).
const vesselKeyOf = (marked) => {
  if (!marked) return ""
  return marked.vesselId != null
    ? `id:${marked.vesselId}`
    : `name:${marked.vesselName ?? ""}`
}

export const treeSelectionActionTypes = {
  VESSEL_MARKED: "VESSEL_MARKED",
  VESSEL_UNMARKED: "VESSEL_UNMARKED",

  TOGGLE_VESSEL_SELECTED: "TOGGLE_VESSEL_SELECTED",
  TOGGLE_VOYAGE_SELECTED: "TOGGLE_VOYAGE_SELECTED",
  ENSURE_VOYAGE_SELECTED: "ENSURE_VOYAGE_SELECTED",
  TOGGLE_ROUTE_SELECTED: "TOGGLE_ROUTE_SELECTED",
  ENSURE_ROUTE_SELECTED: "ENSURE_ROUTE_SELECTED",
  ROUTE_DESELECTED: "ROUTE_DESELECTED",

  TOGGLE_VESSEL_EXPANDED: "TOGGLE_VESSEL_EXPANDED",
  TOGGLE_VOYAGE_EXPANDED: "TOGGLE_VOYAGE_EXPANDED",
  ENSURE_VESSEL_EXPANDED: "ENSURE_VESSEL_EXPANDED",
  ENSURE_VOYAGE_EXPANDED: "ENSURE_VOYAGE_EXPANDED",

  SET_VESSEL_SEARCH_TERM: "SET_VESSEL_SEARCH_TERM",
  CLEAR_ALL_SELECTIONS: "CLEAR_ALL_SELECTIONS",
  CLEAR_SELECTIONS_FOR_VESSEL: "CLEAR_SELECTIONS_FOR_VESSEL",
}

export const initialTreeSelectionState = {
  ui: {
    markedVessel: null,
    selectedVesselIds: [],
    selectedVoyageIds: [],
    selectedRouteIds: [],
    expandedVesselIds: [],
    expandedVoyageIds: [],
    vesselSearchTerm: "",
  },
}

const CLEARED_SELECTION_UI = {
  selectedVesselIds: [],
  selectedVoyageIds: [],
  selectedRouteIds: [],
  expandedVesselIds: [],
  expandedVoyageIds: [],
}

const withUnique = (arr, value) => (arr.includes(value) ? arr : [...arr, value])
const without = (arr, value) => arr.filter((x) => x !== value)

const withUiPatch = (state, patch) => ({
  ...state,
  ui: {
    ...state.ui,
    ...patch,
  },
})
const toggleExpandedUiList = (state, key, id) => {
  const list = state.ui[key] ?? []
  return withUiPatch(state, {
    [key]: list.includes(id) ? without(list, id) : withUnique(list, id),
  })
}
const ensureExpandedUiList = (state, key, id) =>
  withUiPatch(state, {
    [key]: withUnique(state.ui[key] ?? [], id),
  })

// Only one voyage may be selected at a time. On a voyage switch every
// selected non-draft route belonged to the previous voyage (invariant), so
// clearing them all is exact — drafts survive, as before. The reported track
// is cleared too so the new voyage starts with a clean map.
const clearOtherVoyageSelections = (state, keepVoyageId) => {
  const otherVoyageIds = state.ui.selectedVoyageIds.filter((id) => id !== keepVoyageId)
  if (otherVoyageIds.length === 0) return state

  return {
    ...state,
    ui: {
      ...state.ui,
      selectedVoyageIds: state.ui.selectedVoyageIds.filter(
        (id) => id === keepVoyageId,
      ),
      selectedRouteIds: state.ui.selectedRouteIds.filter(isDraftSelectionId),
    },
  }
}

const selectVoyage = (state, vesselId, voyageId) => {
  const base = clearOtherVoyageSelections(state, voyageId)
  return {
    ...base,
    ui: {
      ...base.ui,
      selectedVesselIds: withUnique(base.ui.selectedVesselIds, vesselId),
      selectedVoyageIds: withUnique(base.ui.selectedVoyageIds, voyageId),
      expandedVesselIds: withUnique(base.ui.expandedVesselIds, vesselId),
      expandedVoyageIds: withUnique(base.ui.expandedVoyageIds, voyageId),
    },
  }
}

const selectRoute = (state, vesselId, voyageId, routeId) => {
  const base = clearOtherVoyageSelections(state, voyageId)
  return {
    ...base,
    ui: {
      ...base.ui,
      selectedVesselIds: withUnique(base.ui.selectedVesselIds, vesselId),
      selectedVoyageIds: withUnique(base.ui.selectedVoyageIds, voyageId),
      selectedRouteIds: withUnique(base.ui.selectedRouteIds, routeId),
    },
  }
}

// Single-vessel selection in practice, so clearing every voyage/route
// selection is exact (drafts survive).
const removeDescendantSelectionsForVessel = (state, vesselId) => ({
  ...state,
  ui: {
    ...state.ui,
    selectedVesselIds: without(state.ui.selectedVesselIds, vesselId),
    selectedVoyageIds: [],
    selectedRouteIds: state.ui.selectedRouteIds.filter(isDraftSelectionId),
    expandedVesselIds: without(state.ui.expandedVesselIds, vesselId),
    expandedVoyageIds: [],
  },
})

export function treeSelectionReducer(state, action) {
  switch (action.type) {
    case treeSelectionActionTypes.VESSEL_MARKED: {
      const vesselName = action.payload?.vesselName ?? ""
      if (!vesselName) return state
      const next = {
        vesselName,
        vesselId: action.payload?.vesselId ?? null,
      }
      const current = state.ui.markedVessel
      // Same vessel (key match, or same name — map-icon marks carry no id):
      // keep the selections. An unsaved draft survives re-marking its own
      // vessel, e.g. on import (f007012 contract).
      const sameVessel =
        current != null &&
        (vesselKeyOf(current) === vesselKeyOf(next) ||
          (current.vesselName ?? "") === vesselName)
      if (sameVessel) {
        // A richer mark may add the id a name-only mark lacked.
        if ((current.vesselId ?? null) === next.vesselId || next.vesselId == null) {
          return state
        }
        return withUiPatch(state, { markedVessel: next })
      }
      return withUiPatch(state, {
        markedVessel: next,
        ...CLEARED_SELECTION_UI,
      })
    }

    case treeSelectionActionTypes.VESSEL_UNMARKED: {
      return withUiPatch(state, {
        markedVessel: null,
        ...CLEARED_SELECTION_UI,
      })
    }

    case treeSelectionActionTypes.TOGGLE_VESSEL_SELECTED: {
      const vesselId = action.payload?.vesselId
      if (vesselId == null) return state

      if (state.ui.selectedVesselIds.includes(vesselId)) {
        return removeDescendantSelectionsForVessel(state, vesselId)
      }

      return {
        ...state,
        ui: {
          ...state.ui,
          selectedVesselIds: withUnique(state.ui.selectedVesselIds, vesselId),
        },
      }
    }

    case treeSelectionActionTypes.TOGGLE_VOYAGE_SELECTED: {
      const vesselId = action.payload?.vesselId
      const voyageId = action.payload?.voyageId
      if (vesselId == null || voyageId == null) return state

      if (state.ui.selectedVoyageIds.includes(voyageId)) {
        // Deselect: every selected non-draft route belongs to this voyage.
        return {
          ...state,
          ui: {
            ...state.ui,
            selectedVoyageIds: without(state.ui.selectedVoyageIds, voyageId),
            selectedRouteIds: state.ui.selectedRouteIds.filter(isDraftSelectionId),
          },
        }
      }

      return selectVoyage(state, vesselId, voyageId)
    }

    case treeSelectionActionTypes.ENSURE_VOYAGE_SELECTED: {
      const vesselId = action.payload?.vesselId
      const voyageId = action.payload?.voyageId
      if (vesselId == null || voyageId == null) return state
      if (state.ui.selectedVoyageIds.includes(voyageId)) return state
      return selectVoyage(state, vesselId, voyageId)
    }

    case treeSelectionActionTypes.TOGGLE_ROUTE_SELECTED: {
      const vesselId = action.payload?.vesselId
      const voyageId = action.payload?.voyageId
      const routeId = action.payload?.routeId
      if (routeId == null) return state

      // Deselecting only needs the routeId (e.g. when a route is deleted, its
      // vessel/voyage ids may be unavailable). Removing it from the selection
      // set is enough.
      if (state.ui.selectedRouteIds.includes(routeId)) {
        const nextSelectedRouteIds = without(state.ui.selectedRouteIds, routeId)

        return {
          ...state,
          ui: {
            ...state.ui,
            selectedRouteIds: nextSelectedRouteIds,
          },
        }
      }

      // Drafts and reported tracks have no tree parents — selecting one is a
      // plain add. With parents supplied (tree click) drafts fall through to
      // the full path so the voyage gets co-selected like today.
      if (vesselId == null || voyageId == null) {
        if (!isParentlessSelectionId(routeId)) return state
        return withUiPatch(state, {
          selectedRouteIds: withUnique(state.ui.selectedRouteIds, routeId),
        })
      }

      return selectRoute(state, vesselId, voyageId, routeId)
    }

    case treeSelectionActionTypes.ENSURE_ROUTE_SELECTED: {
      const vesselId = action.payload?.vesselId
      const voyageId = action.payload?.voyageId
      const routeId = action.payload?.routeId
      if (vesselId == null || voyageId == null || routeId == null) return state
      if (state.ui.selectedRouteIds.includes(routeId)) return state
      return selectRoute(state, vesselId, voyageId, routeId)
    }

    case treeSelectionActionTypes.ROUTE_DESELECTED: {
      // Idempotent deselect — safe to dispatch after another clear in the
      // same batch (a toggle would re-add the id there).
      const routeId = action.payload?.routeId
      if (routeId == null || !state.ui.selectedRouteIds.includes(routeId)) {
        return state
      }
      return withUiPatch(state, {
        selectedRouteIds: without(state.ui.selectedRouteIds, routeId),
      })
    }

    case treeSelectionActionTypes.TOGGLE_VESSEL_EXPANDED: {
      const vesselId = action.payload?.vesselId
      if (vesselId == null) return state
      return toggleExpandedUiList(state, "expandedVesselIds", vesselId)
    }

    case treeSelectionActionTypes.TOGGLE_VOYAGE_EXPANDED: {
      const voyageId = action.payload?.voyageId
      if (voyageId == null) return state
      return toggleExpandedUiList(state, "expandedVoyageIds", voyageId)
    }

    case treeSelectionActionTypes.ENSURE_VESSEL_EXPANDED: {
      const vesselId = action.payload?.vesselId
      if (vesselId == null) return state
      return ensureExpandedUiList(state, "expandedVesselIds", vesselId)
    }

    case treeSelectionActionTypes.ENSURE_VOYAGE_EXPANDED: {
      const voyageId = action.payload?.voyageId
      if (voyageId == null) return state
      return ensureExpandedUiList(state, "expandedVoyageIds", voyageId)
    }

    case treeSelectionActionTypes.SET_VESSEL_SEARCH_TERM: {
      return {
        ...state,
        ui: {
          ...state.ui,
          vesselSearchTerm: action.payload?.term ?? "",
        },
      }
    }

    case treeSelectionActionTypes.CLEAR_ALL_SELECTIONS: {
      // Keeps the marked vessel — for resets that stay in the vessel's
      // context. Currently unused by the UI (deselecting happens via
      // VESSEL_UNMARKED when leaving the vessel); kept as reducer API.
      return withUiPatch(state, CLEARED_SELECTION_UI)
    }

    case treeSelectionActionTypes.CLEAR_SELECTIONS_FOR_VESSEL: {
      const vesselId = action.payload?.vesselId
      if (vesselId == null) return state
      return removeDescendantSelectionsForVessel(state, vesselId)
    }

    default:
      return state
  }
}

export const treeSelectionActions = {
  vesselMarked: (vesselName, vesselId = null) => ({
    type: treeSelectionActionTypes.VESSEL_MARKED,
    payload: { vesselName, vesselId },
  }),
  vesselUnmarked: () => ({
    type: treeSelectionActionTypes.VESSEL_UNMARKED,
  }),
  toggleVesselSelected: (vesselId) => ({
    type: treeSelectionActionTypes.TOGGLE_VESSEL_SELECTED,
    payload: { vesselId },
  }),
  toggleVoyageSelected: (vesselId, voyageId) => ({
    type: treeSelectionActionTypes.TOGGLE_VOYAGE_SELECTED,
    payload: { vesselId, voyageId },
  }),
  ensureVoyageSelected: (vesselId, voyageId) => ({
    type: treeSelectionActionTypes.ENSURE_VOYAGE_SELECTED,
    payload: { vesselId, voyageId },
  }),
  ensureRouteSelected: (vesselId, voyageId, routeId) => ({
    type: treeSelectionActionTypes.ENSURE_ROUTE_SELECTED,
    payload: { vesselId, voyageId, routeId },
  }),
  toggleRouteSelected: (vesselId, voyageId, routeId) => ({
    type: treeSelectionActionTypes.TOGGLE_ROUTE_SELECTED,
    payload: { vesselId, voyageId, routeId },
  }),
  deselectRoute: (routeId) => ({
    type: treeSelectionActionTypes.ROUTE_DESELECTED,
    payload: { routeId },
  }),
  toggleVesselExpanded: (vesselId) => ({
    type: treeSelectionActionTypes.TOGGLE_VESSEL_EXPANDED,
    payload: { vesselId },
  }),
  toggleVoyageExpanded: (voyageId) => ({
    type: treeSelectionActionTypes.TOGGLE_VOYAGE_EXPANDED,
    payload: { voyageId },
  }),
  ensureVesselExpanded: (vesselId) => ({
    type: treeSelectionActionTypes.ENSURE_VESSEL_EXPANDED,
    payload: { vesselId },
  }),
  ensureVoyageExpanded: (voyageId) => ({
    type: treeSelectionActionTypes.ENSURE_VOYAGE_EXPANDED,
    payload: { voyageId },
  }),
  setVesselSearchTerm: (term) => ({
    type: treeSelectionActionTypes.SET_VESSEL_SEARCH_TERM,
    payload: { term },
  }),
  clearAllSelections: () => ({
    type: treeSelectionActionTypes.CLEAR_ALL_SELECTIONS,
  }),
  clearSelectionsForVessel: (vesselId) => ({
    type: treeSelectionActionTypes.CLEAR_SELECTIONS_FOR_VESSEL,
    payload: { vesselId },
  }),
}
